import React, { useState } from 'react';
import { ChevronUp } from 'lucide-react';
import { Check } from 'lucide-react';

const FAQPage = () => {
  const [openItems, setOpenItems] = useState(
    new Set([
      'category-0',
      'category-1',
      'category-2',
      'category-3',
      'category-4',
      'general-1',
      'general-2',
      'general-3',
      'general-4',
      'general-5',
      'reg-1',
      'reg-2',
      'reg-3',
      'reg-4',
      'trainer-1',
      'trainer-2',
      'trainer-3',
      'logistics-1',
      'logistics-2',
      'logistics-3',
      'other-1',
      'other-2',
      'other-3',
      'other-4',
    ])
  );

  const faqCategories = [
    {
      category: 'General FAQs',
      items: [
        {
          id: 'general-1',
          question: 'What types of training programs do you offer?',
          answer: 'We offer hands-on public training programs in various areas of information security such as Web Hacking, Malware Analysis, Reverse Engineering, Cloud Security, Incident Response, and many other topics.',
        },
        {
          id: 'general-2',
          question: 'Who are these training sessions for?',
          answer: 'Our sessions are designed for cybersecurity professionals, IT administrators, security engineers, consultants, developers, students, and anyone interested in advancing their practical skills in information security.',
        },
        {
          id: 'general-3',
          question: 'Do I need prior experience to attend?',
          answer: 'Some courses are beginner-friendly, while others require intermediate or advanced technical knowledge. Each course page clearly specifies the prerequisites.',
        },
        {
          id: 'general-4',
          question: 'How are the training sessions conducted?',
          answer: 'We conduct both in-person workshops and private corporate training sessions.',
        },
        {
          id: 'general-5',
          question: 'What do participants receive after completing the training?',
          answer: (
            <ul className="space-y-2 ml-0 list-none">
              <li className="flex gap-2 m-0">
                <span style={{ color: 'var(--accent-primary)' }} className="flex-shrink-0">•</span>
                <span>Certificate of completion</span>
              </li>
              <li className="flex gap-2 m-0">
                <span style={{ color: 'var(--accent-primary)' }} className="flex-shrink-0">•</span>
                <span>Course materials (slides, lab guides, scripts)</span>
              </li>
              <li className="flex gap-2 m-0">
                <span style={{ color: 'var(--accent-primary)' }} className="flex-shrink-0">•</span>
                <span>Access to lab environments for post-training practice (where applicable)</span>
              </li>
            </ul>
          ),
        },
      ],
    },
    {
      category: 'Registration & Payments',
      items: [
        {
          id: 'reg-1',
          question: 'How can I register for a training session?',
          answer: 'You can register directly through our website by selecting the course, filling out the registration form, and completing payment.',
        },
        {
          id: 'reg-2',
          question: 'What payment methods do you accept?',
          answer: (
            <ul className="space-y-2 ml-0 list-none">
              <li className="flex gap-2 m-0">
                <span style={{ color: 'var(--accent-primary)' }} className="flex-shrink-0">•</span>
                <span>All major credit/debit cards, net banking, and UPI</span>
              </li>
              <li className="flex gap-2 m-0">
                <span style={{ color: 'var(--accent-primary)' }} className="flex-shrink-0">•</span>
                <span>International payments via supported gateways</span>
              </li>
              <li className="flex gap-2 m-0">
                <span style={{ color: 'var(--accent-primary)' }} className="flex-shrink-0">•</span>
                <span>Bank transfer for corporate bookings (on request)</span>
              </li>
            </ul>
          ),
        },
        {
          id: 'reg-3',
          question: 'Do you offer group or corporate discounts?',
          answer: 'Yes, we provide special rates for group registrations and corporate bookings. Please contact our team for details.',
        },
        {
          id: 'reg-4',
          question: 'Can I cancel or transfer my registration?',
          answer: 'We do not offer cancellations, but your registration can be transferred to another participant from your organization with prior notice.',
        },
      ],
    },
    {
      category: 'Trainers & Content',
      items: [
        {
          id: 'trainer-1',
          question: 'Who conducts the training sessions?',
          answer: 'All sessions are led by industry practitioners, researchers, and security experts with real-world experience.',
        },
        {
          id: 'trainer-2',
          question: 'How are the courses structured?',
          answer: 'Our courses are highly practical — combining lectures, demonstrations, and hands-on exercises for effective learning.',
        },
        {
          id: 'trainer-3',
          question: 'Will I get access to labs after the training?',
          answer: 'Yes. Most courses provide limited post-training lab access so participants can continue practicing.',
        },
      ],
    },
    {
      category: 'Logistics & Support',
      items: [
        {
          id: 'logistics-1',
          question: 'What should I bring to the training?',
          answer: (
            <ul className="space-y-2 ml-0 list-none">
              <li className="flex gap-2 m-0">
                <span style={{ color: 'var(--accent-primary)' }} className="flex-shrink-0">•</span>
                <span>A laptop that meets the system requirements mentioned in the course details</span>
              </li>
              <li className="flex gap-2 m-0">
                <span style={{ color: 'var(--accent-primary)' }} className="flex-shrink-0">•</span>
                <span>Additional tools/software as communicated in advance</span>
              </li>
            </ul>
          ),
        },
        {
          id: 'logistics-2',
          question: 'Will training materials be provided?',
          answer: 'Yes, you will receive all relevant materials such as slides, lab instructions, and code samples either digitally or printed.',
        },
        {
          id: 'logistics-3',
          question: 'What support do you provide after training?',
          answer: 'We provide post-training email support for queries related to course content. Some programs also include access to private discussion groups.',
        },
      ],
    },
    {
      category: 'Other Common Questions',
      items: [
        {
          id: 'other-1',
          question: 'Do you offer customized corporate training?',
          answer: 'Yes, we design customized in-house programs tailored to your organization\'s needs.',
        },
        {
          id: 'other-2',
          question: 'How often do you update your course content?',
          answer: 'Our courses are regularly updated to include the latest tools, techniques, and industry practices.',
        },
        {
          id: 'other-3',
          question: 'In which language will the training be conducted?',
          answer: 'All trainings are conducted in English unless otherwise specified.',
        },
        {
          id: 'other-4',
          question: 'Will food and refreshments be provided?',
          answer: 'For in-person sessions, lunch and tea/coffee are usually included. Details will be shared in the joining instructions.',
        },
      ],
    },
  ];

  const toggleItem = (id) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id);
    } else {
      newOpenItems.add(id);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <div className="min-h-screen pt-20 pb-16" data-testid="faq-page">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute inset-0">
          <div
            className="absolute top-20 right-20 w-96 h-96 rounded-full opacity-10 blur-3xl"
            style={{
              background: 'radial-gradient(circle, var(--accent-primary), transparent)',
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Frequently Asked Questions
            </h1>
            <p className="text-lg md:text-xl max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
              Find answers to common questions about our training programs, registration, and support
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-12 relative z-10">
        <div className="container mx-auto px-4 md:px-6">
          <div className="space-y-8">
            {faqCategories.map((categoryData, categoryIndex) => (
              <div
                key={categoryIndex}
                className="border rounded-lg overflow-hidden transition-all duration-200 backdrop-blur-sm"
                style={{
                  borderColor: 'var(--border-color)',
                  backgroundColor: 'var(--bg-secondary)',
                  backgroundImage: 'linear-gradient(to bottom, var(--bg-secondary), var(--bg-secondary))',
                }}
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleItem(`category-${categoryIndex}`)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:opacity-80 transition-opacity border-b"
                  style={{
                    borderColor: 'var(--border-color)',
                  }}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <span
                      className="font-bold text-lg"
                      style={{ color: 'var(--accent-primary)' }}
                    >
                      {categoryData.category}
                    </span>
                  </div>
                  <ChevronUp
                    size={20}
                    className="flex-shrink-0 transition-transform duration-200"
                    style={{
                      transform: openItems.has(`category-${categoryIndex}`)
                        ? 'rotate(0deg)'
                        : 'rotate(180deg)',
                      color: 'var(--text-muted)',
                    }}
                  />
                </button>

                {/* Category Items */}
                {openItems.has(`category-${categoryIndex}`) && (
                  <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                    {categoryData.items.map((item, itemIndex) => (
                      <div
                        key={item.id}
                        className="px-6 py-4 flex gap-4 transition-all duration-200"
                        style={{
                          borderLeftColor: 'var(--accent-primary)',
                          borderLeftWidth: itemIndex === 0 ? '4px' : '0',
                        }}
                      >
                        <div className="flex-shrink-0 mt-1 flex items-center justify-center w-6 h-6 rounded-full" style={{
                          backgroundColor: 'var(--accent-primary)',
                        }}>
                          <Check
                            size={16}
                            style={{
                              color: 'var(--bg-primary)',
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4
                            className="font-semibold text-base mb-2"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {item.question}
                          </h4>
                          <div
                            style={{ color: 'var(--text-secondary)' }}
                            className="text-sm space-y-2"
                          >
                            {typeof item.answer === 'string' ? (
                              <p className="m-0">{item.answer}</p>
                            ) : (
                              item.answer
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
            Didn't find what you're looking for?
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-3 rounded-lg font-semibold transition-all duration-200 hover:opacity-80"
            style={{
              backgroundColor: 'var(--accent-primary)',
              color: 'var(--bg-primary)',
            }}
          >
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
